import { isArray, isBoolean } from "lodash-es";
import { BackendTier } from "../auth/auth";
import { ModelType } from "../global-types/customization-types";
import { setOidcSettings } from "../../web-apis/oidc-settings";

export const DEPLOYMENT_CONFIGURATION_FILE_NAME = 'config.json';

/** Deployment mode of this entire Configuration UI environment -- either cloud or local. */
export enum DeploymentMode {
    NotSet = 'NotSet',
    Cockpit = 'cockpit',
    Cloud = 'cloud',
    Local = 'local',
}

export type DocumentLink = {
    label: string;
    url: string;
}

/**
 * Represents information related to MVision.
 *
 * @property ifuUrl - Optional URL for the IFU (Instructions For Use) and/or other related documents.
 * @property email - Optional email address associated with MVision.
 * @property webAddress - Optional web address for MVision.
 * @property address - Optional office address details. If defined, all fields must be given.
 * @property address.company - Name of the company.
 * @property address.street - Street address of the office.
 * @property address.location - Location or city of the office.
 */
export type MVisionInfo = {
    /** URL linking to a MVision IFU (Instructions for Use) document. */
    ifuUrl?: string,
    /** Email address for MVision. */
    email?: string,
    /** Web address for MVision. */
    webAddress?: string,

    /** Office address */
    address?: {
        company: string,
        street: string,
        location: string,
    }
}

export enum DisabledFeature {
    AccessRights = 'AccessRights',
    RegexAssistant = 'RegexAssistant',
    RoiOperationAssistant = 'RoiOperationAssistant',
    RoiOperationValidator = 'RoiOperationValidator',
}

export enum SupportedModelType {
    Adapt = 'Adapt',
    Contour = 'Contour',
    Dose = 'Dose',
    Image = 'Image',
}

export const convertSupportedModelTypeToActualModelType = (supportedModelType: SupportedModelType) => {
    switch (supportedModelType) {
        case SupportedModelType.Adapt:
            return ModelType.Adapt;
        case SupportedModelType.Contour:
            return ModelType.Contouring;
        case SupportedModelType.Dose:
            return ModelType.Dose;
        case SupportedModelType.Image:
            return ModelType.Image;
        default:
            throw new Error(`Invalid supported model type: ${supportedModelType}`);
    }
}

export const convertModelTypeToSupportedModelType = (modelType: ModelType): SupportedModelType | undefined => {
    switch (modelType) {
        case ModelType.Adapt:
            return SupportedModelType.Adapt;
        case ModelType.Contouring:
            return SupportedModelType.Contour;
        case ModelType.Dose:
            return SupportedModelType.Dose;
        case ModelType.Image:
            return SupportedModelType.Image;
        default:
            return undefined;
    }
}

/**
 * Deployment configuration file. Is placed into a MVision Configuration deployment and is used to configure particulars of that deployment.
 * File is read upon app initialization and is used to set up some fundamental app configurations.
 */
export type DeploymentConfigInfo = {
    /** Deployment mode of this app deployment/installation. */
    deploymentMode: DeploymentMode,
    /** The URL in which a CLOUD or LOCAL backend is located, including any required port number. Not used in cockpit mode. */
    backendUrl?: string;
    /** @deprecated Will be removed in a future release. The tier of the cloud backend. Not used in local or cockpit mode. */
    cloudBackendTier?: BackendTier;
    /** Port number (as string) used in cockpit environment. Not used in cloud mode. */
    cockpitBackendPort?: string;
    /** Folder tag used for fetching certain files in cockpit environments. Not used in cloud mode. */
    cockpitEnvironmentTag?: string;
    /** In local mode, whether to use the browser URL as the backend URL. Defaults to false. backendUrl is not used if true.
     * Security note: This trusts the origin from which the user accesses the application. Ensure this is used in trusted 
     * environments only. */
    localUseBrowserUrlAsBackendUrl?: boolean;
    /** URL linking to a dashboard deployment. */
    dashboardUrl?: string;
    /** URL linking to a workspace+ UI deployment. */
    workspaceUrl?: string;
    /** URL linking to a regex website. */
    regexHelpUrl?: string;
    /** Optional name for the backend. Not used in UI as the app doesn't currently support multiple backends. Not used in local mode. */
    backendName?: string;
    /** Link to a ROI customization operations manual. */
    operationsManualUrl?: string;
    /** Link to default ROI coding scheme values. */
    defaultRoiCodesUrl?: string;
    /** Omit any localizations from UI that match these lang keys (e.g.: ['en-US', 'fr-FR', 'pseudo']) */
    omitLocalizationsFromUi?: string[];
    /** Daemon config page can optionally be disabled altogether. */
    disableDaemonConfigPage: boolean;
    /** List of document links to be shown in the UI. */
    documentLinks?: DocumentLink[];
    /** List of features that are disabled in this deployment. */
    disabledFeatures: DisabledFeature[];
    /** List of model types that are supported in this deployment. */
    supportedModelTypes: SupportedModelType[];
    /** Optional MVision information, such as IFU URL, email, web address, and address. */
    mvisionInfo?: MVisionInfo;
    /** URL for the AI assistant Portal API (e.g., https://portal.mvision.ai/api/v1). Required if assistant features are enabled. */
    assistantApiUrl?: string;
    /** The tier of this deployment, e.g. "clinical" or "nonclinical". Required. */
    tier: string;
    /** The region identifier for cloud deployments (e.g. "us", "eu", "dev"). Required in cloud mode, optional otherwise. */
    cloudRegion?: string;

    /** Identity-broker issuer URL (e.g. https://sso.mvision.ai). Set together with oidcClientId. */
    oidcAuthority?: string;
    /** Client id of this app at the identity broker. Set together with oidcAuthority. */
    oidcClientId?: string;
    /** If true, all customization saves will use the force flag, bypassing concurrent save checks. Defaults to false. */
    alwaysForceSave?: boolean;
    /** If true, logs all Cockpit HTTP requests and responses to the browser console for debugging. Only used in cockpit mode. Defaults to false. */
    cockpitDebugMode?: boolean;
}


// Function to validate if an object is a valid DocumentLink
function isValidDocumentLink(link: unknown): link is DocumentLink {
    return (
        typeof link === 'object' && link !== null &&
        'label' in link && typeof link.label === 'string' &&
        'url' in link && typeof link.url === 'string'
    );
}

/** Returns true if queried feature is DISABLED in current deployment, false if it is ENABLED. */
export const isFeatureDisabled = (config: DeploymentConfigInfo, feature: DisabledFeature): boolean => {
    return config.disabledFeatures.includes(feature);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const generateDeploymentConfigInfo = (jsonObject: any): DeploymentConfigInfo | undefined => {
    if ('deploymentMode' in jsonObject && Object.values(DeploymentMode).includes(jsonObject['deploymentMode'])) {

        const validTierValues = ['clinical', 'nonclinical'];
        if (!('tier' in jsonObject) || typeof jsonObject['tier'] !== 'string' ||
            !validTierValues.includes(jsonObject['tier'].toLowerCase())) {
            throw new Error('tier not specified or invalid in configuration. Must be "clinical" or "nonclinical".');
        }

        const deploymentConfigInfo: DeploymentConfigInfo = {
            deploymentMode: jsonObject['deploymentMode'],
            tier: jsonObject['tier'].toLowerCase(),
            disableDaemonConfigPage: false,
            disabledFeatures: [],

            // support all model types if nothing else has been specified
            supportedModelTypes: [SupportedModelType.Contour, SupportedModelType.Dose, SupportedModelType.Adapt, SupportedModelType.Image],
        }

        if ('cloudRegion' in jsonObject && typeof jsonObject['cloudRegion'] === 'string') {
            deploymentConfigInfo.cloudRegion = jsonObject['cloudRegion'];
        }

        // Identity-broker (ZITADEL) login. Both values must be present for the
        // broker path to activate; otherwise the app keeps its Entra behaviour.
        // Deliberately runtime config, not build-time, so one image can be
        // deployed to environments that authenticate differently.
        const oidcAuthority = jsonObject['oidcAuthority'];
        const oidcClientId = jsonObject['oidcClientId'];
        if (typeof oidcAuthority === 'string' && oidcAuthority.length > 0
            && typeof oidcClientId === 'string' && oidcClientId.length > 0) {
            deploymentConfigInfo.oidcAuthority = oidcAuthority;
            deploymentConfigInfo.oidcClientId = oidcClientId;
            setOidcSettings({ authority: oidcAuthority, clientId: oidcClientId });
        } else {
            setOidcSettings(undefined);
        }

        if ('backendUrl' in jsonObject) {
            deploymentConfigInfo.backendUrl = jsonObject['backendUrl'];
        }
        // @deprecated cloudBackendTier will be removed in a future release.
        if ('cloudBackendTier' in jsonObject && Object.values(BackendTier).includes(jsonObject['cloudBackendTier'])) {
            deploymentConfigInfo.cloudBackendTier = jsonObject['cloudBackendTier'];
        }
        if ('cockpitBackendPort' in jsonObject) {
            deploymentConfigInfo.cockpitBackendPort = jsonObject['cockpitBackendPort'];
        }
        if ('cockpitEnvironmentTag' in jsonObject) {
            deploymentConfigInfo.cockpitEnvironmentTag = jsonObject['cockpitEnvironmentTag'];
        }
        if ('localUseBrowserUrlAsBackendUrl' in jsonObject && isBoolean(jsonObject['localUseBrowserUrlAsBackendUrl'])) {
            deploymentConfigInfo.localUseBrowserUrlAsBackendUrl = jsonObject['localUseBrowserUrlAsBackendUrl'];
        }
        if ('dashboardUrl' in jsonObject) {
            deploymentConfigInfo.dashboardUrl = jsonObject['dashboardUrl'];
        }
        if ('workspaceUrl' in jsonObject) {
            deploymentConfigInfo.workspaceUrl = jsonObject['workspaceUrl'];
        }
        if ('backendName' in jsonObject) {
            deploymentConfigInfo.backendName = jsonObject['backendName'];
        }
        if ('regexHelpUrl' in jsonObject) {
            deploymentConfigInfo.regexHelpUrl = jsonObject['regexHelpUrl'];
        }
        if ('operationsManualUrl' in jsonObject) {
            deploymentConfigInfo.operationsManualUrl = jsonObject['operationsManualUrl'];
        }
        if ('defaultRoiCodesUrl' in jsonObject) {
            deploymentConfigInfo.defaultRoiCodesUrl = jsonObject['defaultRoiCodesUrl'];
        }
        if ('omitLocalizationsFromUi' in jsonObject) {
            deploymentConfigInfo.omitLocalizationsFromUi = jsonObject['omitLocalizationsFromUi'];
        }
        if ('disableDaemonConfigPage' in jsonObject) {
            // although daemon config page is NOT disabled by default, in an unclear situation it will get disabled
            deploymentConfigInfo.disableDaemonConfigPage = isBoolean(jsonObject['disableDaemonConfigPage']) ? jsonObject['disableDaemonConfigPage'] : true;
        }

        if ('mvisionInfo' in jsonObject && jsonObject['mvisionInfo'] != null && typeof jsonObject['mvisionInfo'] === 'object') {
            const mvisionInfo = jsonObject['mvisionInfo'];
            deploymentConfigInfo.mvisionInfo = {
                ifuUrl: mvisionInfo['ifuUrl'] || undefined,
                email: mvisionInfo['email'] || undefined,
                webAddress: mvisionInfo['webAddress'] || undefined,
                address: mvisionInfo.address ? {
                    company: mvisionInfo.address?.company || '',
                    street: mvisionInfo.address?.street || '',
                    location: mvisionInfo.address?.location || ''
                } : undefined,
            };
        }

        // check for features that have been disabled in config file
        if ('disabledFeatures' in jsonObject && isArray(jsonObject['disabledFeatures'])) {
            const disabledFeatures: DisabledFeature[] = [];
            for (const disabledFeature of jsonObject['disabledFeatures']) {
                if (Object.values(DisabledFeature).includes(disabledFeature)) {
                    disabledFeatures.push(disabledFeature);
                }
            }
            deploymentConfigInfo.disabledFeatures = disabledFeatures;
        }

        // check for supported model types in config file
        if ('supportedModelTypes' in jsonObject && isArray(jsonObject['supportedModelTypes'])) {
            const supportedModelTypes: SupportedModelType[] = [];
            for (const supportedModelType of jsonObject['supportedModelTypes']) {
                if (Object.values(SupportedModelType).includes(supportedModelType)) {
                    supportedModelTypes.push(supportedModelType);
                }
            }

            // use default supported model types if the array was empty
            if (supportedModelTypes.length > 0) {
                deploymentConfigInfo.supportedModelTypes = supportedModelTypes;
            }
        }

        if ('assistantApiUrl' in jsonObject) {
            deploymentConfigInfo.assistantApiUrl = jsonObject['assistantApiUrl'];
        }

        if ('documentLinks' in jsonObject && Array.isArray(jsonObject['documentLinks'])) {
            const validLinks = [];

            for (const link of jsonObject['documentLinks']) {
                if (isValidDocumentLink(link)) {
                    validLinks.push(link);
                }
            }
            // Assign only valid links to deploymentConfigInfo.documentLinks
            deploymentConfigInfo.documentLinks = validLinks;
        }

        if ('alwaysForceSave' in jsonObject && isBoolean(jsonObject['alwaysForceSave'])) {
            deploymentConfigInfo.alwaysForceSave = jsonObject['alwaysForceSave'];
        }

        if ('cockpitDebugMode' in jsonObject && isBoolean(jsonObject['cockpitDebugMode'])) {
            deploymentConfigInfo.cockpitDebugMode = jsonObject['cockpitDebugMode'];
        }

        // ensure required fields have been set depending on deployment mode
        if (deploymentConfigInfo.deploymentMode === DeploymentMode.Cloud) {
            if (!deploymentConfigInfo.backendUrl) { throw new Error('backendUrl not specified in configuration'); }
            if (!deploymentConfigInfo.cloudBackendTier) { throw new Error('cloudBackendTier not specified in configuration'); }
            if (!deploymentConfigInfo.cloudRegion) { throw new Error('cloudRegion not specified in configuration'); }
        }
        else if (deploymentConfigInfo.deploymentMode === DeploymentMode.Local) {
            if (!deploymentConfigInfo.backendUrl && !deploymentConfigInfo.localUseBrowserUrlAsBackendUrl) {
                throw new Error('backendUrl not specified in configuration');
            }
        }
        else if (deploymentConfigInfo.deploymentMode === DeploymentMode.Cockpit) {
            if (!deploymentConfigInfo.cockpitBackendPort) { throw new Error('cockpitBackendPort not specified in configuration'); }
            if (!deploymentConfigInfo.cockpitEnvironmentTag) { throw new Error('cockpitEnvironmentTag not specified in configuration'); }
        }

        // If assistantApiUrl is not defined at all, force-disable all assistant features.
        // If it IS defined but empty/invalid, warn but leave features enabled (they'll throw on use).
        if (!('assistantApiUrl' in jsonObject)) {
            const assistantFeatures = [DisabledFeature.RegexAssistant, DisabledFeature.RoiOperationAssistant, DisabledFeature.RoiOperationValidator];
            for (const feature of assistantFeatures) {
                if (!deploymentConfigInfo.disabledFeatures.includes(feature)) {
                    deploymentConfigInfo.disabledFeatures.push(feature);
                }
            }
        } else if (!deploymentConfigInfo.assistantApiUrl) {
            console.warn('Warning: assistantApiUrl is defined but empty in config.json. Assistant features will fail on use.');
        }

        return deploymentConfigInfo;
    }

    throw new Error('No supported deployment set in config.json.');
}
